// JOBE-0118: Admin landing page — host for the Dispatcher / Agents /
// Channels sub-pages. Renders a titlebar + sub-tab nav and delegates
// the body to React Router's <Outlet/> for the matched child route.
//
// Routing decision (see specs/in-progress/JOBE-0118-admin-landing-page.md
// Decision 1): nested routes, not `?tab=`. The three sub-paths
// (/admin/dispatcher, /admin/agents, /admin/channels) already existed
// before this spec and are referenced from other pages + tests; the
// AdminPage wrapper preserves those URLs and only adds tab chrome.

import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useDocumentTitle } from '../lib/useDocumentTitle.js';
import './admin.css';

const TABS = [
  { to: '/admin/dispatcher', label: 'Dispatcher' },
  { to: '/admin/agents', label: 'Agents' },
  { to: '/admin/channels', label: 'Channels' },
  // Eval Metrics removed — relocated to PromptOps > Suites (JOBE-0159.5).
  // Redirect: /admin/eval-metrics → /promptops/suites?id=memory-twelve-metric
  { to: '/admin/feature-flags', label: 'Feature Flags' },
];

export function AdminPage() {
  // JOBE-0202: derive active tab from the URL so we can stamp
  // aria-selected onto each NavLink. role="tab" without aria-selected
  // is invalid for assistive tech — the active state was only conveyed
  // via the .admin-tab-active CSS class previously.
  const location = useLocation();
  // JOBE-0193: default title for /admin; sub-pages can override.
  useDocumentTitle('Admin');
  return (
    <div className="admin-page" data-testid="admin-page">
      <header className="admin-page-titlebar">
        <h1 className="admin-page-title">Admin</h1>
      </header>
      <nav
        className="admin-tab-nav"
        role="tablist"
        aria-label="Admin sub-tabs"
        data-testid="admin-tab-nav"
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            // NavLink applies its active className when the current URL
            // matches the link target. We want Agents active on
            // /admin/agents but NOT on /admin/agents/:name (that's the
            // detail route which renders without admin chrome anyway).
            // `end` makes the match exact; combined with the detail
            // routes being siblings outside this Outlet, that gives the
            // right behaviour.
            end
            className={({ isActive }) =>
              `admin-tab-button${isActive ? ' admin-tab-active' : ''}`
            }
            role="tab"
            aria-selected={location.pathname === tab.to}
            data-testid={`admin-tab-${tab.label.toLowerCase()}`}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <div className="admin-page-body" data-testid="admin-page-body">
        <Outlet />
      </div>
    </div>
  );
}
